import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  Device,
  TriggerConfig,
  TriggerScenario,
  TriggerStop,
  TriggerType,
} from "../api/types";
import { ErrorBox, Loading } from "../components/common";
import { useI18n, type Translate } from "../i18n";

// Mirrors TriggerCreate / TriggerConfig on the server, so a bad value is
// explained next to the field instead of coming back as a 422.
const NAME_PATTERN = /^[a-zA-Z0-9._-]+$/;
const NAME_MAX = 128;
const DURATION_MIN = 5;
const DURATION_MAX = 1800;
const DURATION_DEFAULT = 60;

interface Draft {
  name: string;
  description: string;
  type: TriggerType;
  package: string;
  text: string;
  stop: TriggerStop;
  /** Kept as typed: clamping on every keystroke turned "1" into 5 under the cursor. */
  duration: string;
}

type Field = "name" | "value" | "duration";
type Problems = Partial<Record<Field, string>>;

const BLANK: Draft = {
  name: "",
  description: "",
  type: "app_launch",
  package: "",
  text: "",
  stop: "window",
  duration: String(DURATION_DEFAULT),
};

function draftFrom(trigger: TriggerScenario): Draft {
  const config = trigger.config;
  return {
    name: trigger.name,
    description: trigger.description ?? "",
    type: config.type,
    package: config.package ?? "",
    text: config.text ?? "",
    stop: config.type === "app_launch" ? (config.stop ?? "window") : "window",
    duration: String(config.duration_s ?? DURATION_DEFAULT),
  };
}

function validate(draft: Draft, t: Translate): Problems {
  const problems: Problems = {};
  const name = draft.name.trim();
  if (!name) problems.name = t("triggers.errNameEmpty");
  else if (name.length > NAME_MAX) problems.name = t("triggers.errNameLong", { max: NAME_MAX });
  else if (!NAME_PATTERN.test(name)) problems.name = t("triggers.errNameChars");

  if (draft.type === "app_launch" && !draft.package.trim())
    problems.value = t("triggers.errPackageEmpty");
  if (draft.type === "text_visible" && !draft.text.trim())
    problems.value = t("triggers.errTextEmpty");

  const duration = Number(draft.duration);
  if (!draft.duration.trim() || !Number.isInteger(duration))
    problems.duration = t("triggers.errDurationInt");
  else if (duration < DURATION_MIN || duration > DURATION_MAX)
    problems.duration = t("triggers.errDurationRange", { min: DURATION_MIN, max: DURATION_MAX });
  return problems;
}

function toConfig(draft: Draft): TriggerConfig {
  const duration_s = Number(draft.duration);
  if (draft.type === "app_launch") {
    return { type: "app_launch", package: draft.package.trim(), stop: draft.stop, duration_s };
  }
  return { type: "text_visible", text: draft.text.trim(), stop: "window", duration_s };
}

/** How long the phone keeps recording once the trigger fires. */
function recordingSummary(config: TriggerConfig, t: Translate): string {
  const cap = config.duration_s;
  if (config.stop === "on_exit") {
    return cap ? t("triggers.recOnExitCap", { n: cap }) : t("triggers.recOnExit");
  }
  return t("triggers.durationShort", { n: cap ?? DURATION_DEFAULT });
}

function Condition({ config }: { config: TriggerConfig }) {
  const { t } = useI18n();
  const app = config.type === "app_launch";
  return (
    <div className="trigger-cond">
      <span className="kind">{app ? t("triggers.condApp") : t("triggers.condText")}</span>
      <code>{app ? config.package : `“${config.text}”`}</code>
    </div>
  );
}

function DeviceChip({ device }: { device: Device }) {
  const { t } = useI18n();
  const status = t(`status.${device.status}`, undefined, device.status.replace(/_/g, " "));
  return (
    <Link
      to={`/devices/${device.id}`}
      className={`device-chip ${device.status}`}
      aria-label={t("triggers.chipAria", { name: device.name, status })}
      title={t("triggers.chipTitle", { name: device.name, status })}
    >
      <i aria-hidden="true" />
      <span>{device.name}</span>
    </Link>
  );
}

/** The whole trigger as one sentence, so the form reads back what it will do. */
function Preview({ draft }: { draft: Draft }) {
  const { t } = useI18n();
  const app = draft.type === "app_launch";
  const subject = app
    ? draft.package.trim() || t("triggers.subjectApp")
    : draft.text.trim()
      ? `“${draft.text.trim()}”`
      : t("triggers.subjectText");
  const seconds = Number(draft.duration);
  const cap =
    Number.isInteger(seconds) && seconds >= DURATION_MIN && seconds <= DURATION_MAX
      ? t("triggers.durationShort", { n: seconds })
      : "…";
  const onExit = app && draft.stop === "on_exit";
  return (
    <p className="trigger-preview">
      {t("triggers.previewWhen")} <strong>{subject}</strong>{" "}
      {app
        ? onExit
          ? t("triggers.previewOpensUntil")
          : t("triggers.previewOpens")
        : t("triggers.previewAppears")}{" "}
      <strong>{cap}</strong>
      {onExit ? t("triggers.previewMaxSuffix") : "."}
    </p>
  );
}

function Field({
  label,
  optional,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  children: (props: {
    id: string;
    "aria-describedby": string;
    "aria-invalid": boolean;
  }) => ReactNode;
}) {
  const { t } = useI18n();
  const id = useId();
  return (
    <div className={`field ${className ?? ""}`}>
      <label htmlFor={id} className="field-label">
        {label}
        {optional && <em>{t("triggers.optional")}</em>}
      </label>
      {children({ id, "aria-describedby": `${id}-note`, "aria-invalid": !!error })}
      <span id={`${id}-note`} className={`field-hint ${error ? "error" : ""}`}>
        {error ?? hint}
      </span>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (value: T) => void;
}) {
  const id = useId();
  return (
    <div className="field fixed">
      <span className="field-label" id={id}>
        {label}
      </span>
      <div className="toolgroup seg" role="radiogroup" aria-labelledby={id}>
        {options.map(([option, text]) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            className={value === option ? "on" : ""}
            onClick={() => onChange(option)}
          >
            {text}
          </button>
        ))}
      </div>
      <span className="field-hint" />
    </div>
  );
}

export default function Triggers() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const triggers = useQuery({ queryKey: ["triggers"], queryFn: api.triggers });
  // The "armed on" chips are read off the fleet, so keep it as fresh as the
  // Devices page does.
  const devices = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.devices(),
    refetchInterval: 5000,
  });

  const [draft, setDraft] = useState<Draft>(BLANK);
  const [editing, setEditing] = useState<TriggerScenario | null>(null);
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [attempted, setAttempted] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLInputElement>(null);
  const durationRef = useRef<HTMLInputElement>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["triggers"] });
    queryClient.invalidateQueries({ queryKey: ["devices"] });
  };

  const create = useMutation({
    mutationFn: (draft: Draft) =>
      api.createTrigger({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        config: toConfig(draft),
      }),
    onSuccess: (created) => {
      reset();
      setNotice(t("triggers.created", { name: created.name }));
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Draft }) =>
      api.updateTrigger(id, { description: draft.description.trim(), config: toConfig(draft) }),
    onSuccess: (saved) => {
      reset();
      setNotice(t("triggers.saved", { name: saved.name }));
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteTrigger(id),
    onSuccess: (_, id) => {
      setConfirmId(null);
      if (editing?.id === id) reset();
      invalidate();
    },
  });

  // Going into edit mode brings the form, and the value being changed, to
  // the operator instead of leaving it below the fold.
  useEffect(() => {
    if (!editing) return;
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    valueRef.current?.focus();
  }, [editing]);

  function reset() {
    setDraft(BLANK);
    setEditing(null);
    setTouched({});
    setAttempted(false);
  }

  function patch(changes: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...changes }));
    setNotice(null);
  }

  function touch(field: Field) {
    setTouched((current) => ({ ...current, [field]: true }));
  }

  function startNew() {
    reset();
    setNotice(null);
    create.reset();
    update.reset();
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    nameRef.current?.focus();
  }

  function startEdit(trigger: TriggerScenario) {
    setDraft(draftFrom(trigger));
    setEditing(trigger);
    setTouched({});
    setAttempted(false);
    setNotice(null);
    setConfirmId(null);
    create.reset();
    update.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const problems = validate(draft, t);
    const first = (["name", "value", "duration"] as Field[]).find((field) => problems[field]);
    if (first) {
      setAttempted(true);
      const target = first === "name" ? nameRef : first === "value" ? valueRef : durationRef;
      target.current?.focus();
      return;
    }
    if (editing) update.mutate({ id: editing.id, draft });
    else create.mutate(draft);
  }

  if (triggers.isLoading) return <Loading what={t("loading.triggers")} />;
  if (triggers.error) return <ErrorBox error={triggers.error} />;

  const list = triggers.data ?? [];
  const armedOn = (triggerId: string) =>
    (devices.data ?? []).filter((d) => d.trigger_scenario_id === triggerId);

  const isApp = draft.type === "app_launch";
  const problems = validate(draft, t);
  // Leaving a field blank is only a problem once the operator tries to save;
  // until then only what they actually typed gets corrected under their eyes.
  const filled: Record<Field, boolean> = {
    name: draft.name.trim() !== "",
    value: (isApp ? draft.package : draft.text).trim() !== "",
    duration: draft.duration.trim() !== "",
  };
  const problem = (field: Field) =>
    attempted || (touched[field] && filled[field]) ? problems[field] : undefined;
  const save = editing ? update : create;
  const editingArmed = editing ? armedOn(editing.id).length : 0;

  return (
    <>
      <div className="page-header">
        <div>
          <h2>{t("triggers.title")}</h2>
          <p className="page-sub">
            {t("triggers.introA")} <Link to="/">{t("triggers.devicePanelLink")}</Link>.
          </p>
        </div>
        <button className="primary" onClick={startNew}>
          {t("triggers.new")}
        </button>
      </div>

      <div className="card table-wrap" style={{ padding: 0 }}>
        <table className="trigger-table">
          <thead>
            <tr>
              <th>{t("triggers.th.name")}</th>
              <th>{t("triggers.th.condition")}</th>
              <th>{t("triggers.th.recording")}</th>
              <th>{t("triggers.th.armedOn")}</th>
              <th>
                <span className="sr-only">{t("triggers.th.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((trigger) => {
              const armed = armedOn(trigger.id);
              if (confirmId === trigger.id) {
                return (
                  <tr key={trigger.id} className="confirm-row">
                    <td colSpan={5}>
                      <div className="row" style={{ flexWrap: "wrap" }}>
                        <span>
                          {t("triggers.deleteConfirm", { name: trigger.name })}{" "}
                          {armed.length > 0
                            ? armed.length === 1
                              ? t("triggers.deleteArmedOne")
                              : t("triggers.deleteArmedMany", { n: armed.length })
                            : t("triggers.deleteGone")}
                        </span>
                        <span className="spacer" />
                        <button
                          className="danger"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(trigger.id)}
                        >
                          {t("triggers.deleteTrigger")}
                        </button>
                        <button onClick={() => setConfirmId(null)}>{t("triggers.keepIt")}</button>
                      </div>
                      {remove.error && <ErrorBox error={remove.error} />}
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={trigger.id} className={editing?.id === trigger.id ? "editing" : undefined}>
                  <td>
                    <div className="name">{trigger.name}</div>
                    {trigger.description && <div className="desc">{trigger.description}</div>}
                  </td>
                  <td>
                    <Condition config={trigger.config} />
                  </td>
                  <td className="nowrap">{recordingSummary(trigger.config, t)}</td>
                  <td>
                    {armed.length === 0 ? (
                      <span className="muted">{t("triggers.notArmed")}</span>
                    ) : (
                      <div className="armed-list">
                        {armed.map((device) => (
                          <DeviceChip key={device.id} device={device} />
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button onClick={() => startEdit(trigger)}>{t("triggers.edit")}</button>
                      <button className="danger" onClick={() => setConfirmId(trigger.id)}>
                        {t("triggers.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr className="empty-row">
                <td colSpan={5}>
                  <p className="muted" style={{ margin: 0 }}>
                    {t("triggers.empty")}
                  </p>
                  <button style={{ marginTop: 10 }} onClick={startNew}>
                    {t("triggers.createFirst")}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form ref={formRef} className="card trigger-form" onSubmit={submit} noValidate>
        <div className="trigger-form-head">
          <h3>
            {editing ? (
              <>
                {t("triggers.edit")} <span className="mono">{editing.name}</span>
              </>
            ) : (
              t("triggers.new")
            )}
          </h3>
          {editing && (
            <button type="button" onClick={startNew}>
              {t("triggers.cancel")}
            </button>
          )}
        </div>
        {editing && (
          <p className="muted" style={{ margin: "-6px 0 0", fontSize: 12 }}>
            {editingArmed > 0
              ? editingArmed === 1
                ? t("triggers.armedNoteOne")
                : t("triggers.armedNoteMany", { n: editingArmed })
              : t("triggers.armedNoteNone")}
          </p>
        )}

        <div className="fields">
          <Field
            label={t("triggers.name")}
            hint={editing ? t("triggers.nameHintEdit") : t("triggers.nameHint")}
            error={problem("name")}
          >
            {(props) => (
              <input
                {...props}
                ref={nameRef}
                placeholder="play-store-open"
                value={draft.name}
                disabled={!!editing}
                maxLength={NAME_MAX}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => patch({ name: e.target.value })}
                onBlur={() => touch("name")}
              />
            )}
          </Field>
          <Field label={t("triggers.description")} optional className="grow">
            {(props) => (
              <input
                {...props}
                placeholder={t("triggers.descriptionPlaceholder")}
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
              />
            )}
          </Field>
        </div>

        <div className="fields">
          <Segmented<TriggerType>
            label={t("triggers.firesWhen")}
            value={draft.type}
            options={[
              ["app_launch", t("triggers.firesApp")],
              ["text_visible", t("triggers.firesText")],
            ]}
            onChange={(type) => {
              patch({ type });
              setTouched((current) => ({ ...current, value: false }));
            }}
          />
          <Field
            label={isApp ? t("triggers.valueApp") : t("triggers.valueText")}
            hint={
              isApp ? t("triggers.valueAppHint") : t("triggers.valueTextHint")
            }
            error={problem("value")}
            className="grow"
          >
            {(props) =>
              isApp ? (
                <input
                  {...props}
                  ref={valueRef}
                  className="mono"
                  placeholder="com.example.app"
                  value={draft.package}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => patch({ package: e.target.value })}
                  onBlur={() => touch("value")}
                />
              ) : (
                <input
                  {...props}
                  ref={valueRef}
                  placeholder="Sign in"
                  value={draft.text}
                  onChange={(e) => patch({ text: e.target.value })}
                  onBlur={() => touch("value")}
                />
              )
            }
          </Field>
        </div>

        <div className="fields">
          {isApp && (
            <Segmented<TriggerStop>
              label={t("triggers.thenRecord")}
              value={draft.stop}
              options={[
                ["window", t("triggers.stopWindow")],
                ["on_exit", t("triggers.stopOnExit")],
              ]}
              onChange={(stop) => patch({ stop })}
            />
          )}
          <Field
            label={
              !isApp
                ? t("triggers.recordFor")
                : draft.stop === "on_exit"
                  ? t("triggers.safetyCap")
                  : t("triggers.window")
            }
            hint={
              isApp && draft.stop === "on_exit"
                ? t("triggers.durationHintCap")
                : t("triggers.durationHint", { min: DURATION_MIN, max: DURATION_MAX })
            }
            error={problem("duration")}
            className="fixed"
          >
            {(props) => (
              <span className="unit-input">
                <input
                  {...props}
                  ref={durationRef}
                  type="number"
                  inputMode="numeric"
                  min={DURATION_MIN}
                  max={DURATION_MAX}
                  step={1}
                  value={draft.duration}
                  onChange={(e) => patch({ duration: e.target.value })}
                  onBlur={() => touch("duration")}
                />
                <span>{t("triggers.seconds")}</span>
              </span>
            )}
          </Field>
        </div>

        <Preview draft={draft} />

        <div className="row trigger-actions">
          <button type="submit" className="primary" disabled={save.isPending}>
            {editing ? t("triggers.save") : t("triggers.create")}
          </button>
          {editing && (
            <button type="button" onClick={startNew}>
              {t("triggers.cancel")}
            </button>
          )}
          {notice && <span className="muted">{notice}</span>}
        </div>
        {save.error &&
          (save.error instanceof ApiError && save.error.status === 409 && create.variables ? (
            <p className="error-text" style={{ margin: 0 }}>
              {t("triggers.duplicateA")} <strong>{create.variables.name.trim()}</strong>{" "}
              {t("triggers.duplicateB")}
            </p>
          ) : (
            <ErrorBox error={save.error} />
          ))}
      </form>
    </>
  );
}
